import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isTenantFixedByHostname,
  resolveTenantSlug,
  useTenant,
} from '../hooks/useTenantContext';
import { getStoredTenantSlug } from '../utils/tenantStorage';
import Button from '../components/ui/Button';
import logo from '../assets/logo.png';
import { Input as PrimitiveInput } from '../components/ui/input';

/**
 * Landing `/`: choose tenant when hostname does not encode one (e.g. localhost, *.replit.app).
 * Subdomain tenants (`*.sliderule.com`, `*.localhost`) skip straight to /portal.
 */
const TenantPickerPage: React.FC = () => {
  const navigate = useNavigate();
  const { setTenantSlug } = useTenant();
  const [slug, setSlug] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isTenantFixedByHostname()) {
      navigate('/portal', { replace: true });
      return;
    }
    if (resolveTenantSlug() !== '') {
      navigate('/portal', { replace: true });
      return;
    }
    const saved = getStoredTenantSlug();
    if (saved) {
      setSlug(saved);
    }
  }, [navigate]);

  const onContinue = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = slug.trim().toLowerCase();
    if (!trimmed) {
      setError('Enter your company identifier (e.g. acme).');
      return;
    }
    if (!/^[a-z0-9][a-z0-9-]{0,126}$/.test(trimmed)) {
      setError('Use lowercase letters, numbers, and hyphens only.');
      return;
    }
    setError(null);
    setTenantSlug(trimmed);
    navigate('/login');
  };

  if (isTenantFixedByHostname() || resolveTenantSlug() !== '') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-ink-subtle">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src={logo}
            alt=""
            className="h-14 w-auto mx-auto mb-4 logo-themed"
          />
          <h1 className="text-2xl font-semibold text-ink tracking-tight">
            Enter your organization URL
          </h1>
        </div>

        <div className="rounded-xl border border-border bg-surface-strong shadow-sm p-6">
          <h2 className="text-lg font-medium text-ink mb-4">
            Organization URL
          </h2>
          <form onSubmit={onContinue} className="space-y-4">
            <div>
              <label
                htmlFor="org-identifier"
                className="block text-sm font-medium text-ink mb-1"
              >
                Company name
              </label>
              <PrimitiveInput
                unstyled
                id="org-identifier"
                type="text"
                value={slug}
                onChange={(ev) => setSlug(ev.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-neon/30"
                placeholder="Company name"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            {error && (
              <p className="text-sm text-danger-600" role="alert">
                {error}
              </p>
            )}
            <div className="flex justify-center pt-1">
              <Button type="submit" className="btn-neon-outline">
                Continue to sign in
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TenantPickerPage;
